(() => {
  const campaignKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
  const safeCode = /^[a-zA-Z0-9._-]{1,100}$/;
  const referencePattern = /^LBT-\d{8}-[A-F0-9]{12}$/;
  const publicPath = /^\/(?:$|(?:pages|collections|products|blogs|policies)(?:\/|$))/;
  const storageKey = 'lbt_inquiry_handoff_v1';
  const leadStorageKey = 'lbt_inquiry_lead_published_v1';
  const leadEventName = 'lbt:inquiry_submitted';
  const publishing = new Set();
  const lifetime = 30 * 60 * 1000;
  const phone = '8619064321680';
  const models = ['GH', 'GV', 'SH', 'SV', 'Multiple models / advice needed'];
  const requests = ['Sample request', 'Wholesale quotation', 'Custom design'];

  const getCampaign = (params) => Object.fromEntries(campaignKeys.flatMap((key) => {
    const value = params.get(key);
    return value && safeCode.test(value) ? [[key, value]] : [];
  }));

  function readPending() {
    try {
      const data = JSON.parse(sessionStorage.getItem(storageKey));
      if (!data || !referencePattern.test(data.reference) || !Number.isFinite(data.savedAt) ||
          data.savedAt > Date.now() || Date.now() - data.savedAt > lifetime) {
        sessionStorage.removeItem(storageKey);
        return null;
      }
      return {
        reference: data.reference,
        model: models.includes(data.model) ? data.model : '',
        request: requests.includes(data.request) ? data.request : '',
        quantity: typeof data.quantity === 'string' && /^[A-Z0-9a-z:; =.()\-]{0,1000}$/.test(data.quantity) ? data.quantity : '',
        campaign: getCampaign(new URLSearchParams(data.campaign || {})),
        path: typeof data.path === 'string' && publicPath.test(data.path) ? data.path : '',
        savedAt: data.savedAt,
        confirmed: data.confirmed === true
      };
    } catch { return null; }
  }

  function savePending(record) {
    try { sessionStorage.setItem(storageKey, JSON.stringify(record)); } catch {}
  }

  function publishedReferences() {
    try {
      const entries = JSON.parse(sessionStorage.getItem(leadStorageKey) || '{}');
      return Object.fromEntries(Object.entries(entries).filter(([reference, time]) =>
        referencePattern.test(reference) && Number.isFinite(time) &&
        time <= Date.now() && Date.now() - time < 24 * 60 * 60 * 1000
      ).slice(-100));
    } catch { return {}; }
  }

  function rememberPublished(reference, remember) {
    try {
      const entries = publishedReferences();
      if (remember) entries[reference] = Date.now();
      else delete entries[reference];
      sessionStorage.setItem(leadStorageKey, JSON.stringify(entries));
      return true;
    } catch { return false; }
  }

  function publishLead(record, attempt = 0) {
    if (!record || window.Shopify?.designMode || !referencePattern.test(record.reference) ||
        publishedReferences()[record.reference] || publishing.has(record.reference)) return;
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') {
      if (attempt < 20) window.setTimeout(() => publishLead(record, attempt + 1), 250);
      return;
    }
    if (!rememberPublished(record.reference, true)) return;
    publishing.add(record.reference);
    const retry = () => {
      rememberPublished(record.reference, false);
      publishing.delete(record.reference);
      if (attempt < 20) window.setTimeout(() => publishLead(record, attempt + 1), 250);
    };
    try {
      const result = publish.call(window.Shopify.analytics, leadEventName, {
        inquiry_reference: record.reference,
        model: record.model,
        request_type: record.request
      });
      Promise.resolve(result).then((accepted) => {
        if (accepted === false) retry();
        else publishing.delete(record.reference);
      }, retry);
    } catch { retry(); }
  }

  function newReference() {
    const bytes = new Uint8Array(6);
    if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
    else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    const date = new Date().toISOString().slice(0, 10).replaceAll('-', '');
    const suffix = Array.from(bytes, (n) => n.toString(16).padStart(2, '0')).join('').toUpperCase();
    return `LBT-${date}-${suffix}`;
  }

  function initialize() {
    const current = new URL(window.location.href);
    const success = Boolean(document.querySelector('.lbt-quote-form [data-lbt-submission-success]'));
    let pending = readPending();
    if (document.querySelector('.lbt-quote-form .lbt-form-error')) {
      try { sessionStorage.removeItem(storageKey); } catch {}
      pending = null;
    }
    if (success && current.searchParams.get('contact_posted') === 'true' && pending &&
        (current.pathname === pending.path || current.pathname === '/pages/contact-us')) {
      pending.confirmed = true;
      savePending(pending);
    }
    const submitted = success && current.searchParams.get('contact_posted') === 'true' && pending?.confirmed ? pending : null;
    const campaign = { ...(submitted?.campaign || {}), ...getCampaign(current.searchParams) };

    if (!window.Shopify?.designMode && current.pathname !== '/pages/contact-us' &&
        current.searchParams.get('contact_posted') === 'true' && success) {
      const target = new URL('/pages/contact-us', current.origin);
      target.searchParams.set('contact_posted', 'true');
      Object.entries(campaign).forEach(([key, value]) => target.searchParams.set(key, value));
      target.hash = 'lbt-quote';
      window.location.replace(target.pathname + target.search + target.hash);
      return;
    }

    publishLead(submitted);

    document.querySelectorAll('a[href]').forEach((link) => {
      if (link.hasAttribute('download')) return;
      let target;
      try { target = new URL(link.getAttribute('href'), current); } catch { return; }
      if (target.origin !== current.origin || !publicPath.test(target.pathname)) return;
      Object.entries(campaign).forEach(([key, value]) => {
        if (!target.searchParams.has(key)) target.searchParams.set(key, value);
      });
      if (Object.keys(campaign).length) link.setAttribute('href', target.pathname + target.search + target.hash);
    });

    function recordFor(form) {
      if (submitted) return { ...submitted, campaign };
      const referenceField = form?.querySelector('[data-lbt-inquiry-reference]');
      if (referenceField && !success && !referencePattern.test(referenceField.value)) referenceField.value = newReference();
      const selectedModel = form?.querySelector('[data-lbt-model]')?.value;
      const selectedRequest = form?.querySelector('[data-lbt-request-type]')?.value;
      const bagUnit = form?.querySelector('[data-lbt-unit]')?.value === 'Bags of 20 pieces';
      const quantities = [];
      let count = 0;
      form?.querySelectorAll('[data-lbt-letter]').forEach((input) => {
        const value = Number(input.value);
        const letter = input.dataset.lbtLetter;
        if (/^[A-Z]$/.test(letter) && Number.isSafeInteger(value) && value > 0 && value <= 10000000) {
          quantities.push(`${letter}: ${value}`);
          count += value;
        }
      });
      const unit = bagUnit ? 'bags of 20 pieces' : 'pieces';
      const total = bagUnit ? `${count} bags = ${count * 20} pieces` : `${count} pieces`;
      return {
        reference: referenceField?.value || '',
        model: models.includes(selectedModel) ? selectedModel : '',
        request: requests.includes(selectedRequest) ? selectedRequest : '',
        quantity: quantities.length ? `${quantities.join('; ')}. Unit: ${unit}. Total: ${total}` : '',
        campaign, path: current.pathname, savedAt: Date.now(), confirmed: false
      };
    }

    function updateLink(link) {
      const form = link.closest('.lbt-quote-form') || document.querySelector('.lbt-quote-form');
      const copy = form?.querySelector('[data-lbt-wa-copy]') || document.querySelector('[data-lbt-wa-copy]');
      if (!copy) return;
      const labels = copy.dataset;
      const record = recordFor(form);
      const lines = [submitted ? labels.lbtWaCopyFollowup : labels.lbtWaCopyStart];
      const add = (key, value) => { if (value && labels[key]) lines.push(`${labels[key]}: ${value}`); };
      add('lbtWaCopyReference', record.reference);
      add('lbtWaCopyModel', record.model);
      add('lbtWaCopyRequest', record.request);
      add('lbtWaCopyQuantity', record.quantity);
      add('lbtWaCopySource', [record.campaign.utm_source, record.campaign.utm_medium].filter(Boolean).join(' / '));
      add('lbtWaCopyCampaign', record.campaign.utm_campaign);
      add('lbtWaCopyCreative', record.campaign.utm_content);
      const url = new URL(`https://wa.me/${phone}`);
      url.searchParams.set('text', lines.filter(Boolean).join('\n'));
      link.href = url.toString();
      form?.querySelectorAll('[data-lbt-reference]').forEach((output) => { output.textContent = record.reference; });
      form?.querySelectorAll('[data-lbt-reference-line]').forEach((line) => { line.hidden = !record.reference; });
    }

    const refreshLinks = () => document.querySelectorAll('[data-lbt-whatsapp]').forEach(updateLink);
    document.querySelectorAll('.lbt-quote-form').forEach((form) => {
      const reference = form.querySelector('[data-lbt-inquiry-reference]');
      if (reference) reference.value = submitted?.reference || (success ? '' :
        (referencePattern.test(reference.value) && !publishedReferences()[reference.value] ? reference.value : newReference()));
      if (form.dataset.lbtHandoffReady) return;
      form.dataset.lbtHandoffReady = 'true';
      form.querySelectorAll('[data-lbt-campaign]').forEach((field) => {
        field.value = campaign[field.dataset.lbtCampaign] || '';
      });
      form.addEventListener('input', refreshLinks);
      form.addEventListener('change', refreshLinks);
      form.addEventListener('submit', (event) => {
        if (event.defaultPrevented) return;
        if (form.dataset.lbtSubmitting === 'true') {
          event.preventDefault();
          return;
        }
        if (!form.checkValidity()) return;
        if (reference && publishedReferences()[reference.value]) reference.value = newReference();
        const record = recordFor(form);
        if (referencePattern.test(record.reference)) savePending({ ...record, savedAt: Date.now(), confirmed: false });
        form.dataset.lbtSubmitting = 'true';
        form.setAttribute('aria-busy', 'true');
        window.setTimeout(() => {
          delete form.dataset.lbtSubmitting;
          form.removeAttribute('aria-busy');
        }, 10000);
      });
    });
    document.querySelectorAll('[data-lbt-whatsapp]').forEach((link) => {
      if (link.dataset.lbtHandoffReady) return;
      link.dataset.lbtHandoffReady = 'true';
      link.addEventListener('click', () => updateLink(link));
      link.addEventListener('focus', () => updateLink(link));
    });
    refreshLinks();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('.lbt-quote-form').forEach((form) => {
      delete form.dataset.lbtSubmitting;
      form.removeAttribute('aria-busy');
    });
    initialize();
  });
})();